import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Globe, MonitorSmartphone, Chrome, MapPin, RefreshCw, Loader2 } from "lucide-react";

const Bar = ({ label, count, max }) => (
  <div className="flex items-center gap-3" data-testid={`traffic-bar-${label}`}>
    <span className="w-28 shrink-0 truncate text-sm text-slate-600" title={label}>{label}</span>
    <div className="h-3 flex-1 overflow-hidden rounded-full bg-slate-100">
      <div className="h-full rounded-full bg-amber-500" style={{ width: `${max ? Math.max(6, (count / max) * 100) : 0}%` }} />
    </div>
    <span className="w-10 shrink-0 text-right text-sm font-semibold text-slate-900">{count}</span>
  </div>
);

const Panel = ({ icon: Icon, title, rows, testid }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <h4 className="mb-4 flex items-center gap-2 font-display font-bold text-slate-900"><Icon className="h-5 w-5 text-amber-600" /> {title}</h4>
      {rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">No data yet.</p>
      ) : (
        <div className="space-y-2.5">{rows.map((r) => <Bar key={r.label} label={r.label} count={r.count} max={max} />)}</div>
      )}
    </div>
  );
};

export default function AdminTraffic() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/traffic").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (loading && !data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-amber-600" /></div>;
  if (!data) return <p className="py-10 text-center text-slate-500">Unable to load traffic.</p>;

  const Stat = ({ label, value }) => (
    <div className="rounded-xl border border-slate-200 bg-white p-4 text-center" data-testid={`traffic-stat-${label}`}>
      <div className="font-display text-3xl font-extrabold text-slate-900">{value}</div>
      <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-traffic">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Visitor analytics for the public welcome page. Location is estimated from the visitor's IP address.</p>
        <button type="button" onClick={load} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 transition-colors hover:border-amber-500 hover:text-amber-600" data-testid="traffic-refresh">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Total visits" value={data.total} />
        <Stat label="Today" value={data.today} />
        <Stat label="This week" value={data.week} />
        <Stat label="This month" value={data.month} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Panel icon={Globe} title="Top countries" rows={data.countries} testid="traffic-countries" />
        <Panel icon={MonitorSmartphone} title="Devices" rows={data.devices} testid="traffic-devices" />
        <Panel icon={Chrome} title="Browsers" rows={data.browsers} testid="traffic-browsers" />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="traffic-recent">
        <h4 className="mb-1 flex items-center gap-2 font-display font-bold text-slate-900"><MapPin className="h-5 w-5 text-amber-600" /> Recent visits</h4>
        <p className="mb-4 text-xs text-slate-400">Latest {data.recent.length} recorded visits ({data.logged} tracked in total).</p>
        <div className="overflow-x-auto rounded-lg border border-slate-100">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2">When</th>
                <th className="px-4 py-2">Location</th>
                <th className="px-4 py-2">Device</th>
                <th className="px-4 py-2">Browser / OS</th>
                <th className="px-4 py-2">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.recent.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No visits recorded yet.</td></tr>
              )}
              {data.recent.map((v) => (
                <tr key={v.id} data-testid={`traffic-row-${v.id}`}>
                  <td className="whitespace-nowrap px-4 py-2 text-slate-500">{v.ts ? new Date(v.ts).toLocaleString() : "—"}</td>
                  <td className="px-4 py-2 text-slate-800">
                    {[v.city, v.region, v.country].filter(Boolean).join(", ") || "Unknown"}
                  </td>
                  <td className="px-4 py-2 text-slate-600">{v.device || "—"}</td>
                  <td className="px-4 py-2 text-slate-600">{[v.browser, v.os].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-2 font-mono text-xs text-slate-400">{v.ip}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
